import type { BaselineType } from '../types';

export interface NormalisedDataPoint {
  originalValue: number | null;
  value: number;
}

export interface NormalisedValuation {
  originalFairValue: number | null;
  fairValue: number | null;
  createdAt: string;
}

export interface NormalisedChartData {
  normalisedEntities: Record<number, NormalisedDataPoint>;
  normalisedValuations: NormalisedValuation[] | undefined;
}

export const normalizeChartData = (
  entities: Record<number, number | null>,
  keys: number[],
  valuations?: { fairValue: number | null; createdAt: string }[] | null,
  baselineType: BaselineType = 'initial'
): NormalisedChartData => {
  // Choose reference price (baseline) based on user preference
  let baseline = 0;

  switch (baselineType) {
    case 'initial':
      baseline = entities[keys[0]] || 1; // Use first price as 100%
      break;
    case 'current':
      baseline = entities[keys[keys.length - 1]] || 1; // Use latest price as 100%
      break;
    case 'average':
      baseline =
        keys.reduce((sum, key) => sum + (entities[key] || 0), 0) /
          keys.length || 1; // Use average as 100%
      break;
  }

  // Convert all share prices to percentage changes
  const normalisedEntities = {} as Record<number, NormalisedDataPoint>;

  keys.forEach((key) => {
    const value = entities[key];

    if (value !== null && value !== undefined && baseline !== 0) {
      normalisedEntities[key] = {
        originalValue: value,
        value: (value / baseline - 1) * 100,
      };
    } else {
      normalisedEntities[key] = {
        originalValue: value,
        value: 0,
      };
    }
  });

  // Convert fair values to percentage changes too
  const normalisedValuations = valuations?.map((v) => ({
    ...v,
    originalFairValue: v.fairValue,
    fairValue:
      v.fairValue && baseline !== 0 ? (v.fairValue / baseline - 1) * 100 : null,
  }));

  return { normalisedEntities, normalisedValuations };
};
